import { useEffect, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Chip,
  Collapse,
  Container,
  Divider,
  Group,
  Modal,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Timeline,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconBookmark,
  IconBookmarkFilled,
  IconCamera,
  IconCameraOff,
  IconChevronDown,
  IconChevronRight,
  IconExternalLink,
  IconScan,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { DeviceHistory, SavedDeviceLookup, TimelineEntry } from "../api/types";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { useCurrentUser } from "../hooks/useCurrentUser";

type SourceFilter = "all" | "device" | "shipment" | "batch" | "shipment_history";

const FILTERS: SourceFilter[] = ["all", "device", "shipment", "batch", "shipment_history"];

function statusColor(status: string): string {
  switch (status) {
    case "returned":
      return "orange";
    case "defect":
      return "red";
    case "restored":
      return "teal";
    case "shipped":
    case "delivered":
      return "blue";
    default:
      return "gray";
  }
}

export function DeviceLookup() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const isAdmin = user.role === "admin";
  const [manual, setManual] = useState("");
  const [cameraOn, setCameraOn] = useState(false);
  const [history, setHistory] = useState<DeviceHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<SourceFilter>("all");
  const [saved, setSaved] = useState<SavedDeviceLookup[]>([]);
  const [savedOpen, setSavedOpen] = useState(true);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [markDefectOpen, setMarkDefectOpen] = useState(false);
  const [defectReason, setDefectReason] = useState("");

  async function loadSaved() {
    try {
      const list = await api.listSavedLookups();
      setSaved(list);
    } catch {
      // soft-fail; the page still works without the list
    }
  }

  useEffect(() => {
    void loadSaved();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initial = params.get("dev_eui");
    if (initial) {
      setManual(initial);
      void lookup(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function lookup(raw: string) {
    const value = raw.trim().replace(/^https?:\/\/[^/]+\//i, "").split(/[/?#]/).pop() ?? "";
    if (!value) return;
    setLoading(true);
    try {
      const result = await api.lookupDevice(value);
      if (!result.found || !result.device) {
        notifications.show({
          color: "red",
          message: t("deviceLookup.notFound", { code: raw }),
        });
        setHistory(null);
        return;
      }
      const full = await api.deviceHistory(result.device.id);
      setHistory(full);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      notifications.show({ color: "red", message: msg });
    } finally {
      setLoading(false);
    }
  }

  async function loadById(deviceId: number) {
    setLoading(true);
    try {
      const full = await api.deviceHistory(deviceId);
      setHistory(full);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      notifications.show({ color: "red", message: msg });
    } finally {
      setLoading(false);
    }
  }

  async function saveCurrent() {
    if (!history?.device) return;
    setSaving(true);
    try {
      await api.saveDeviceLookup(history.device.id, saveNote.trim() || null);
      setSaveModalOpen(false);
      setSaveNote("");
      await loadSaved();
      notifications.show({ color: "teal", message: t("deviceLookup.savedOk") });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      notifications.show({ color: "red", message: msg });
    } finally {
      setSaving(false);
    }
  }

  async function unsave(deviceId: number) {
    try {
      await api.unsaveDeviceLookup(deviceId);
      await loadSaved();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      notifications.show({ color: "red", message: msg });
    }
  }

  const currentDeviceIsSaved = !!(history?.device && saved.some(s => s.device_id === history!.device!.id));

  const filtered = (history?.timeline ?? []).filter((e: TimelineEntry) =>
    filter === "all" ? true : (e.source ?? "device") === filter,
  );

  return (
    <Container size="lg">
      <Title order={3} mb="md">
        {t("deviceLookup.title")}
      </Title>

      {saved.length > 0 && (
        <Card withBorder mb="md">
          <Group justify="space-between" wrap="nowrap" style={{ cursor: "pointer" }}
            onClick={() => setSavedOpen(o => !o)}>
            <Group gap="xs">
              {savedOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              <Title order={5}>
                {t("deviceLookup.savedSection", { count: saved.length })}
              </Title>
            </Group>
          </Group>
          <Collapse in={savedOpen}>
            <ResponsiveTable
              mobile={{
                rows: saved,
                rowKey: (s) => s.device_id,
                title: (s) => <Text span ff="monospace" size="sm" fw={600}>{s.dev_eui.toLowerCase()}</Text>,
                subtitle: (s) => s.model ?? undefined,
                fields: [
                  { label: t("deviceLookup.currentShipment"), render: (s) => s.current_shipment?.code ?? "—" },
                  { label: t("deviceLookup.note"), render: (s) => s.note ?? "—" },
                ],
                actions: (s) => (
                  <Group gap="xs" wrap="nowrap">
                    <Button size="sm" variant="light" onClick={() => void loadById(s.device_id)}>
                      {t("deviceLookup.savedLoad")}
                    </Button>
                    <ActionIcon color="red" variant="subtle" size="lg" onClick={() => void unsave(s.device_id)}
                      title={t("deviceLookup.savedRemove")}>
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Group>
                ),
              }}
            >
            <Table mt="sm" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>DEV_EUI</Table.Th>
                  <Table.Th>{t("deviceLookup.model")}</Table.Th>
                  <Table.Th>{t("deviceLookup.currentShipment")}</Table.Th>
                  <Table.Th>{t("deviceLookup.note")}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {saved.map((s) => (
                  <Table.Tr key={s.device_id}>
                    <Table.Td>
                      <Text ff="monospace" size="sm">{s.dev_eui.toLowerCase()}</Text>
                    </Table.Td>
                    <Table.Td>{s.model ?? "—"}</Table.Td>
                    <Table.Td>{s.current_shipment?.code ?? "—"}</Table.Td>
                    <Table.Td>{s.note ?? "—"}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap" justify="flex-end">
                        <Button size="xs" variant="light" onClick={() => void loadById(s.device_id)}>
                          {t("deviceLookup.savedLoad")}
                        </Button>
                        <ActionIcon color="red" variant="subtle" onClick={() => void unsave(s.device_id)}
                          title={t("deviceLookup.savedRemove")}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </ResponsiveTable>
          </Collapse>
        </Card>
      )}

      <Paper withBorder p="md" mb="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder={t("deviceLookup.searchPlaceholder")}
            value={manual}
            onChange={(e) => setManual(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void lookup(manual);
            }}
            leftSection={<IconScan size={16} />}
            style={{ flex: "1 1 280px" }}
            autoFocus
          />
          <Switch
            label={cameraOn ? t("scanner.cameraOn") : t("scanner.cameraOff")}
            checked={cameraOn}
            onChange={(e) => setCameraOn(e.currentTarget.checked)}
            thumbIcon={cameraOn ? <IconCamera size={12} /> : <IconCameraOff size={12} />}
          />
        </Group>
        {cameraOn && (
          <div style={{ maxWidth: 480, marginTop: 12 }}>
            <BarcodeScanner active onDetect={(raw) => void lookup(raw)} />
          </div>
        )}
      </Paper>

      {loading && <Text c="dimmed">{t("common.loading")}</Text>}

      {history?.device && (
        <Stack gap="md">
          <Card withBorder>
            <Group justify="space-between" mb="xs" wrap="wrap">
              <div>
                <Text size="xs" c="dimmed">DEV_EUI</Text>
                <Text ff="monospace" size="lg" fw={600}>
                  {history.device.dev_eui.toLowerCase()}
                </Text>
              </div>
              <Group gap="xs">
                <Badge size="lg" color={statusColor(history.device.status)} variant="filled">
                  {history.device.status}
                </Badge>
                <Button
                  size="xs"
                  variant={currentDeviceIsSaved ? "filled" : "light"}
                  color="teal"
                  leftSection={currentDeviceIsSaved ? <IconBookmarkFilled size={14} /> : <IconBookmark size={14} />}
                  onClick={() => {
                    if (currentDeviceIsSaved) {
                      void unsave(history!.device.id);
                    } else {
                      setSaveNote("");
                      setSaveModalOpen(true);
                    }
                  }}
                >
                  {currentDeviceIsSaved ? t("deviceLookup.savedToggle") : t("deviceLookup.saveButton")}
                </Button>
                <Anchor component={Link} to={`/rma?dev_eui=${history.device.dev_eui}`}>
                  <Group gap={4}>
                    <IconExternalLink size={14} />
                    <Text size="sm">{t("deviceLookup.openInRma")}</Text>
                  </Group>
                </Anchor>
                {isAdmin && history.device.status !== "defect" && (
                  <Button color="red" variant="light" size="xs" onClick={() => setMarkDefectOpen(true)}>
                    {t("deviceLookup.markDefect")}
                  </Button>
                )}
              </Group>
            </Group>
            <Divider my="sm" />
            <Group gap="xl" wrap="wrap">
              <InfoBlock
                label={t("antennas.productType")}
                value={
                  history.device.product_type_slug === "antenna"
                    ? t("antennas.typeAntenna")
                    : t("antennas.typeModule")
                }
              />
              {history.device.product_type_slug === "antenna" && (
                <InfoBlock
                  label={t("antennas.linkedModuleOpen")}
                  value={
                    history.device.linked_dev_eui ? (
                      <Anchor
                        component="button"
                        type="button"
                        ff="monospace"
                        onClick={() => {
                          const eui = history.device.linked_dev_eui!.toLowerCase();
                          setManual(eui);
                          void lookup(eui);
                        }}
                      >
                        {history.device.linked_dev_eui.toLowerCase()}
                      </Anchor>
                    ) : "—"
                  }
                />
              )}
              <InfoBlock label={t("deviceLookup.model")} value={history.device.model ?? "—"} />
              <InfoBlock label={t("deviceLookup.serial")} value={history.device.serial_number ?? "—"} />
              <InfoBlock
                label={t("deviceLookup.batch")}
                value={
                  history.batch ? (
                    <Anchor component={Link} to={`/batches/${history.batch.id}`}>
                      {history.batch.code}
                    </Anchor>
                  ) : "—"
                }
              />
              <InfoBlock
                label={t("deviceLookup.currentShipment")}
                value={
                  history.current_shipment ? (
                    <Anchor component={Link} to={`/shipments/${history.current_shipment.id}`}>
                      {history.current_shipment.code}
                    </Anchor>
                  ) : "—"
                }
              />
              <InfoBlock label={t("deviceLookup.customer")} value={history.customer?.name ?? "—"} />
            </Group>
          </Card>

          <Card withBorder>
            <Title order={5} mb="sm">{t("deviceLookup.shipmentHistory")}</Title>
            {history.shipment_history.length === 0 ? (
              <Text c="dimmed" size="sm">{t("deviceLookup.shipmentHistoryEmpty")}</Text>
            ) : (
              <ResponsiveTable
                mobile={{
                  rows: history.shipment_history,
                  rowKey: (_h) => history.shipment_history.indexOf(_h),
                  title: (h) => h.shipment ? (
                    <Anchor component={Link} to={`/shipments/${h.shipment.id}`} size="sm" fw={600}>
                      {h.shipment.code}
                    </Anchor>
                  ) : "—",
                  subtitle: (h) => new Date(h.entered_at).toLocaleString(),
                  fields: [
                    { label: t("deviceLookup.customer"), render: (h) => h.shipment?.customer_name ?? "—" },
                    { label: t("deviceLookup.left"), render: (h) => (h.left_at ? new Date(h.left_at).toLocaleString() : "—") },
                    { label: t("deviceLookup.reason"), render: (h) => h.reason ?? "—" },
                    { label: t("deviceLookup.movedBy"), render: (h) => h.moved_by ?? "—" },
                  ],
                }}
              >
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("deviceLookup.shipment")}</Table.Th>
                    <Table.Th>{t("deviceLookup.customer")}</Table.Th>
                    <Table.Th>{t("deviceLookup.entered")}</Table.Th>
                    <Table.Th>{t("deviceLookup.left")}</Table.Th>
                    <Table.Th>{t("deviceLookup.reason")}</Table.Th>
                    <Table.Th>{t("deviceLookup.movedBy")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {history.shipment_history.map((h, idx) => (
                    <Table.Tr key={idx}>
                      <Table.Td>
                        {h.shipment ? (
                          <Anchor component={Link} to={`/shipments/${h.shipment.id}`}>
                            {h.shipment.code}
                          </Anchor>
                        ) : "—"}
                      </Table.Td>
                      <Table.Td>{h.shipment?.customer_name ?? "—"}</Table.Td>
                      <Table.Td>{new Date(h.entered_at).toLocaleString()}</Table.Td>
                      <Table.Td>{h.left_at ? new Date(h.left_at).toLocaleString() : "—"}</Table.Td>
                      <Table.Td>{h.reason ?? "—"}</Table.Td>
                      <Table.Td>{h.moved_by ?? "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </ResponsiveTable>
            )}
          </Card>

          <Card withBorder>
            <Group justify="space-between" mb="sm" wrap="wrap">
              <Title order={5}>{t("deviceLookup.timeline")}</Title>
              <Chip.Group multiple={false} value={filter} onChange={(v) => setFilter(v as SourceFilter)}>
                <Group gap="xs">
                  {FILTERS.map((f) => (
                    <Chip key={f} value={f} size="xs">
                      {t(`deviceLookup.filter_${f}`)}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>
            </Group>
            {filtered.length === 0 ? (
              <Text c="dimmed" size="sm">{t("deviceLookup.timelineEmpty")}</Text>
            ) : (
              <Timeline bulletSize={20} lineWidth={2}>
                {filtered.map((e) => (
                  <Timeline.Item key={String(e.id)} title={e.action}>
                    <Text size="xs" c="dimmed">
                      {new Date(e.created_at).toLocaleString()}
                      {e.user || e.username ? ` • ${e.user ?? e.username}` : ""}
                      {e.context?.shipment_code ? ` • ${e.context.shipment_code}` : ""}
                    </Text>
                    {e.payload != null && (
                      <Text component="pre" size="xs" style={{ whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(e.payload, null, 2)}
                      </Text>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Card>
        </Stack>
      )}
      <Modal
        opened={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        title={t("deviceLookup.saveModalTitle")}
        centered
      >
        <TextInput
          label={t("deviceLookup.note")}
          placeholder={t("deviceLookup.notePlaceholder")}
          value={saveNote}
          onChange={(e) => setSaveNote(e.currentTarget.value)}
          mb="md"
          autoFocus
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => setSaveModalOpen(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button color="teal" onClick={() => void saveCurrent()} loading={saving}>
            {t("deviceLookup.saveButton")}
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={markDefectOpen}
        onClose={() => setMarkDefectOpen(false)}
        title={t("deviceLookup.markDefectTitle")}
        centered
      >
        <Stack>
          <TextInput
            label={t("deviceLookup.markDefectReason")}
            value={defectReason}
            onChange={(e) => setDefectReason(e.currentTarget.value)}
            placeholder={t("deviceLookup.markDefectPlaceholder")}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setMarkDefectOpen(false)}>{t("common.cancel")}</Button>
            <Button color="red" onClick={async () => {
              if (!history?.device) return;
              try {
                await api.markDeviceReturned(history.device.id, "defect", defectReason || "marked defect");
                setMarkDefectOpen(false);
                setDefectReason("");
                const full = await api.deviceHistory(history.device.id);
                setHistory(full);
                notifications.show({ color: "teal", message: t("deviceLookup.markDefectOk") });
              } catch (e) {
                notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
              }
            }}>{t("deviceLookup.markDefect")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

function InfoBlock({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm">{value}</Text>
    </div>
  );
}
